<div
  class:timestamps-with-base-time={$baseTime !== undefined}
  class:toggle-timestamps={hideTimestampTextInternal}
  style={`
    ${MoveListComponent.CSSVariables.MaxMoveTimeTaken}: ${$largestTimestamp}; 
    ${MoveListComponent.CSSVariables.TimestampWidth}: ${$maxTimestampWidth}px`
  }>
  {#each $moveListRows as row, i (row.get().renderKey) }
    {#if isDataCommentRow(row)}
      {#if !$commentsHidden}
        <CommentRow
          moveListRow={row}
        />
      {/if}
    {:else if isDataMovesRow(row)}
      {#if row.get().depth === 0}
        <MainLineRow
          moveListRow={row}
          {showTimestamps}
          hasMainlineRowAfter={lastMainLineRowIndex > i}
          />
      {:else}
        <VariationRow
          moveListRow={row}
          nextMoveListRow={$moveListRows[i + 1]}
        />
      {/if}
    {/if}
  {/each}

  {#if $result}
      <div class='main-line-row move-list-row result-row'>
        <!-- having this wrapped in a 'main-line-move' div is hacky
        way of styling and retrieving the game result in tests -->
        <span class='game-result'>
          {$result}
          {#if resultTooltipProps}
            <Tooltip {...resultTooltipProps}/>
          {/if}
        </span>
      </div>
  {/if}
</div>

{#if $contextMenu.isOpen && $contextMenu.enabled && $selectedNodeEl}
  <ContextMenu
    menuProps={contextMenu}
    {selectedNodeKeys}
    selectedNodeRect={$selectedNodeEl.getBoundingClientRect()}
    {game}
  />
{/if}

{#if $variationPopover.isOpen && $selectedNodeEl}
  <VariationsPopover
    {variationPopover}
    selectedNodeRect={$selectedNodeEl.getBoundingClientRect()}
    {eventHandlers}
    {textType}
  />
{/if}

<script lang="ts">
import type { EventHandlers } from 'components/move-list/state-machine/types';
import type { ContextShape, Store } from 'components/move-list/store/types';
import VariationsPopover from 'components/move-list/components/variations/popover.svelte';
import { getResultTooltipProps } from 'components/move-list/helpers/get-tooltip-props';
import {
  MoveListComponent,
  isDataCommentRow,
  isDataMovesRow,
  isMovesRow,
} from 'components/move-list/config/types';
import ContextMenu from 'components/move-list/components/context-menu/context-menu.svelte';
import Tooltip from 'components/move-list/components/tooltip.svelte';
import CommentRow from 'components/move-list/components/comment-row.svelte';
import MainLineRow from 'components/move-list/components/main-line-row.svelte';
import VariationRow from 'components/move-list/components/variations/variation-row.svelte';
import type { GameAPI } from 'chessboard/types';
import { onMount, setContext } from 'svelte';
import { MoveListPlugin } from 'chessboard/plugins/move-list/config/types';
import type { Data } from 'src/utils/svelte/data';
import { getMoveNodeEl } from './helpers/dom/get-move-node-el';

export let data: Store;
export let eventHandlers: EventHandlers;
export let hideTimestampText: boolean;
export let showTimestamps: boolean;
export let game: GameAPI;

const {
  moveListRows,
  selectedNodeKeys,
  selectedNodeEl,
  textType,
  result,
  maxTimestampWidth,
  largestTimestamp,
  baseTime,
  contextMenu,
  variationPopover,
  gameMetadata,
  commentsHidden,
  translations,
  scrollContainer,
  moveListEl,
  finishedInitialLoad,
} = data;

setContext<ContextShape>(MoveListPlugin.Name, {
  store: data,
  eventHandlers,
  game,
});

$: resultTooltipProps = getResultTooltipProps({
  translations: $translations,
  gameMetadata: $gameMetadata,
});
$: container = $scrollContainer ?? $moveListEl;
$: lastMainLineRowIndex = getLastMainlineRowIndex($moveListRows);
$: hideTimestampTextInternal = hideTimestampText;

const resizeObserver = new ResizeObserver(handleContainerResize);

onMount(() => {
  resizeObserver.observe(container);
  container.addEventListener('scroll', handleScrollOrResize);
  window.addEventListener('scroll', handleScrollOrResize);

  const unsubscribeToFinishedInitialLoad = finishedInitialLoad.subscribe(reinitializeScroll);

  return () => {
    resizeObserver.disconnect();
    container.removeEventListener('scroll', handleScrollOrResize);
    window.removeEventListener('scroll', handleScrollOrResize);
    unsubscribeToFinishedInitialLoad();
  };
});

/**
 * handle container resize
 */
function handleContainerResize(entries) {
  handleScrollOrResize();

  if (hideTimestampText !== undefined) {
    return;
  }

  const containerWidth = entries[0].contentRect.width;
  const minWidth = 305 + maxTimestampWidth.get();

  if (containerWidth < minWidth) {
    hideTimestampTextInternal = true;
  } else {
    hideTimestampTextInternal = false;
  }
}

/**
 * handles a scroll or resize event
 */
function handleScrollOrResize() {
  const popoverProps = variationPopover.get();
  if (popoverProps.isOpen) {
    variationPopover.set({ ...popoverProps, isOpen: false });
  }

  const contextMenuProps = contextMenu.get();
  if (contextMenuProps.isOpen) {
    contextMenu.set({ ...contextMenuProps, isOpen: false });
  }
}

/**
 * reinitialize the scroll position of the move list
 */
function reinitializeScroll(hasFinishedInitialLoad: boolean) {
  if (hasFinishedInitialLoad) {
    return;
  }

  const nodeKeys = data.selectedNodeKeys.get();
  if (!nodeKeys) {
    return;
  }

  const nodeEl = getMoveNodeEl(
    data.moveListEl.get(),
    nodeKeys.ids.line,
    nodeKeys.ids.move,
  ) as HTMLElement | null;

  if (!nodeEl) {
    return;
  }

  eventHandlers.scrollToMoveNode(nodeEl, { scrollToTop: true });
  finishedInitialLoad.set(true);
}

/**
 * gets the last mainline row index
 */
function getLastMainlineRowIndex(rows: Data<MoveListComponent.Row>[]) {
  // need to update ES target to use Array.findLastIndex (?). type-check complains
  // iterate backwards to find the last mainline row
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i].get();
    if (isMovesRow(row) && row.depth === 0) {
      return i;
    }
  }
  return -1;
}
</script>
